import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useId } from 'react';

import type { AppArcSection, ArcLeftOut, ArcPreviewResponse } from '@/api/generated';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { CardSkeleton } from '@/components/ui/Skeletons';

import { ArcPreviewCard } from './ArcPreviewCard';
import { ArcPreviewHeader } from './ArcPreviewHeader';
import {
  ARC_PREVIEW_WIDTH_PX,
  CARDS_NOTE,
  EMPTY_HINT,
  EMPTY_TITLE,
  LEFT_OUT_HEADING,
  NOTHING_LEFT_OUT,
  PREVIEW_DESCRIPTION,
  PREVIEW_LABEL,
  leftOutWords,
} from './arcPreview';

interface ArcPreviewDialogProps {
  open: boolean;
  /** The backend's reply for the working copy; undefined while it is on its way. */
  reply: ArcPreviewResponse | undefined;
  /** The backend's sentence for a refused or failed preview. */
  failure: string | null;
  /**
   * Sends the working copy again. Given when the request failed (a fault, or
   * no reply), not when the backend refused the copy: the same copy gets the
   * same answer (B20).
   */
  onRetry?: () => void;
  onClose: () => void;
}

/** One section as the app draws it: its name, its line, and its cards. */
function PreviewSection({ section }: { section: AppArcSection }) {
  const headingId = useId();
  return (
    <Box
      component="section"
      aria-labelledby={headingId}
      data-testid={`arc-preview-section-${section.id}-group`}
      sx={{ display: 'grid', gap: 1 }}
    >
      <Box>
        <Typography
          id={headingId}
          component="h3"
          variant="subtitle1"
          sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
          data-testid={`arc-preview-section-${section.id}-title`}
        >
          {section.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid={`arc-preview-section-${section.id}-caption`}
        >
          {section.summary}
        </Typography>
      </Box>
      {/* Cards, not buttons: Preview opens nothing (D19). */}
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
        {section.items.map((card) => (
          <ArcPreviewCard key={card.id} card={card} />
        ))}
      </Box>
      {/* The app's way into the whole section; here it opens nothing (D19). */}
      <Box>
        <Button
          variant="ghost"
          size="sm"
          disabled
          data-testid={`arc-preview-section-${section.id}-view-all-button`}
        >
          {section.view_all_label}
        </Button>
      </Box>
    </Box>
  );
}

/** Beside the frame: what the app will not show, and why, in words (D17). */
function LeftOut({ entries }: { entries: ArcLeftOut[] }) {
  const headingId = useId();
  return (
    <Box
      component="section"
      aria-labelledby={headingId}
      data-testid="arc-preview-left-out-section"
      sx={{ flex: '1 1 240px', minWidth: 0, maxWidth: 480 }}
    >
      <Typography
        id={headingId}
        component="h3"
        variant="subtitle1"
        sx={{ fontWeight: 600, mb: 1 }}
      >
        {LEFT_OUT_HEADING}
      </Typography>
      {entries.length === 0 ? (
        <Typography variant="body2" data-testid="arc-preview-left-out-empty">
          {NOTHING_LEFT_OUT}
        </Typography>
      ) : (
        <Box
          component="ul"
          sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.5 }}
          data-testid="arc-preview-left-out-list"
        >
          {entries.map((entry) => (
            <Typography
              key={`${entry.section_id}-${entry.item_id ?? 'section'}`}
              component="li"
              variant="body2"
              sx={{ overflowWrap: 'anywhere' }}
              data-testid={`arc-preview-left-out-${entry.item_id ?? entry.section_id}-label`}
            >
              {leftOutWords(entry)}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * Preview (organization-arc, group 7; US-033, B15 to B18, D16 to D19, D24).
 *
 * A full-screen dialog rather than a page, so the builder's unsaved edits are
 * neither lost nor prompted about (D18). The page posts the working copy as
 * Save Draft would send it when Preview is pressed (`useArcPreview`); the
 * backend applies it, answers the app's reply for everyone (D39), and keeps
 * nothing. The phone frame is `ARC_PREVIEW_WIDTH_PX` wide and labelled
 * "Preview", with no "You're viewing" banner (D24). Its cards open nothing,
 * and a note says so (D19). Beside it, what was left out, in words. Closing
 * changes nothing on the page.
 *
 * Group 8 adds the header, each type's card look, "View all", the empty and
 * error states with Try again, and the rest of the accessibility work (D23).
 */
export function ArcPreviewDialog({
  open,
  reply,
  failure,
  onRetry,
  onClose,
}: ArcPreviewDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={PREVIEW_LABEL}
      description={PREVIEW_DESCRIPTION}
      fullScreen
      testId="arc-preview-dialog"
    >
      {failure ? (
        <ErrorState title={failure} onRetry={onRetry} testId="arc-preview" />
      ) : !reply ? (
        <Box data-testid="arc-preview-loading" sx={{ maxWidth: ARC_PREVIEW_WIDTH_PX }}>
          <CardSkeleton lines={4} label="Loading the preview" />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'flex-start' }}>
          <Box sx={{ display: 'grid', gap: 1, maxWidth: '100%' }}>
            <Typography
              variant="overline"
              color="text.secondary"
              data-testid="arc-preview-frame-label"
            >
              {PREVIEW_LABEL}
            </Typography>
            <Paper
              component="section"
              aria-label={PREVIEW_LABEL}
              variant="outlined"
              data-testid="arc-preview-frame-section"
              sx={{
                width: ARC_PREVIEW_WIDTH_PX,
                maxWidth: '100%',
                borderRadius: 4,
                p: 2,
                display: 'grid',
                gap: 2,
                alignContent: 'start',
              }}
            >
              <ArcPreviewHeader arc={reply.arc} organization={reply.organization} />
              {reply.arc.sections.length === 0 ? (
                <EmptyState
                  title={EMPTY_TITLE}
                  description={EMPTY_HINT}
                  testId="arc-preview"
                />
              ) : (
                reply.arc.sections.map((section) => (
                  <PreviewSection key={section.id} section={section} />
                ))
              )}
            </Paper>
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="arc-preview-cards-hint"
            >
              {CARDS_NOTE}
            </Typography>
          </Box>
          <LeftOut entries={reply.left_out} />
        </Box>
      )}
    </Dialog>
  );
}
